import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MODES, MODE_MAP } from "@/lib/modes";
import { Search, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ScanResultSheet from "@/components/ScanResultSheet";

export default function HistoryPage({ favoritesOnly = false }) {
  const [scans, setScans] = useState([]);
  const [q, setQ] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (modeFilter) params.mode = modeFilter;
    if (favoritesOnly) params.favorite = true;
    try {
      const r = await api.get("/scans", { params });
      setScans(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, modeFilter, favoritesOnly]);

  const del = async (id) => {
    try { await api.delete(`/scans/${id}`); setScans((s) => s.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const toggleFav = async (scan, favorite) => {
    try {
      const r = await api.patch(`/scans/${scan.id}/favorite`, { favorite });
      setScans((s) => s.map((x) => (x.id === scan.id ? r.data : x)));
      setSelected(r.data);
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="min-h-screen pb-28 pt-6 px-4 max-w-md mx-auto">
      <h1 className="font-heading text-3xl font-bold">{favoritesOnly ? "Saved" : "History"}</h1>
      <p className="text-slate-400 text-sm">{favoritesOnly ? "Your favorite Jixxeo scans" : "Every scan you've made"}</p>

      <div className="mt-4 flex gap-2">
        <div className="flex-1 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2 px-3">
          <Search size={16} className="text-slate-500" />
          <input data-testid="history-search" placeholder="Search scans" value={q} onChange={(e) => setQ(e.target.value)}
            className="bg-transparent outline-none flex-1 text-sm" />
        </div>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
        <button data-testid="filter-mode-all" onClick={() => setModeFilter("")} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${!modeFilter ? "bg-cyan-400/15 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-slate-400"}`}>All</button>
        {MODES.map((m) => (
          <button key={m.id} data-testid={`filter-mode-${m.id}`} onClick={() => setModeFilter(m.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${modeFilter === m.id ? "text-white" : "bg-white/5 border-white/10 text-slate-400"}`}
            style={modeFilter === m.id ? { background: `${m.color}22`, borderColor: `${m.color}88`, color: m.color } : {}}
          >{m.label}</button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {scans.length === 0 && (
          <div className="jx-glass rounded-2xl p-6 text-center text-slate-400 text-sm">Nothing here yet — go scan something.</div>
        )}
        {scans.map((s) => {
          const m = MODE_MAP[s.mode] || MODE_MAP.jixxeo_lens;
          return (
            <div key={s.id} data-testid={`history-item-${s.id}`} className="jx-glass rounded-2xl p-3 flex items-center gap-3">
              {s.thumbnail && <img src={s.thumbnail} alt="" onClick={() => setSelected(s)} className="w-14 h-14 rounded-xl object-cover border border-white/10 cursor-pointer" />}
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelected(s)}>
                <p className="font-heading font-semibold text-sm truncate">{s.title}</p>
                <p className="text-[11px] text-slate-400 truncate">{s.subtitle}</p>
                <p className="text-[10px] font-mono-data uppercase mt-0.5" style={{ color: m.color }}>{m.label} · {s.confidence.toFixed(0)}%</p>
              </div>
              <button data-testid={`history-fav-${s.id}`} onClick={() => toggleFav(s, !s.favorite)} className={`p-2 rounded-xl ${s.favorite ? "text-amber-300" : "text-slate-500"}`}>
                <Star size={16} fill={s.favorite ? "currentColor" : "none"} />
              </button>
              <button data-testid={`history-del-${s.id}`} onClick={() => del(s.id)} className="p-2 rounded-xl text-slate-500 hover:text-rose-400"><Trash2 size={16} /></button>
            </div>
          );
        })}
      </div>

      {selected && <ScanResultSheet result={selected} onClose={() => setSelected(null)} onToggleFavorite={toggleFav} />}
    </div>
  );
}
